import React, { useMemo, useState, useRef, useEffect } from 'react';
import TrodoWordmark from 'features/onboarding/components/TrodoWordmark';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { UserPlus, X, ChevronDown, Check } from 'lucide-react';
import { useTeam } from 'shared/context/teamContext';
import { enterDemoTeam } from 'shared/utils/demoTeam';
import axiosInstance from 'shared/services/api/axiosInstance';
import MembersPage from 'features/settings/components/panels/MembersPage';

const PANEL_BG = 'var(--surface-sunken)';
const ACCENT = 'var(--trodo-primary)';

const MANUAL_DOCS_URL = 'https://docs.trodo.ai/observability/features/instrumentation/guide';

/**
 * Screen 1 of the onboarding install wall. Shown for any team that has never
 * ingested a run. Gives the user a copy-paste agent prompt (with their real
 * site ID substituted) that installs the Trodo tracing skill end-to-end, plus a
 * way to explore a fully-populated demo workspace instead.
 */
const InstallProjectPage = () => {
  const navigate = useNavigate();
  const { team: teamParam } = useParams();
  const { selectedTeam, setSelectedTeam } = useTeam();

  const [copied, setCopied] = useState(false);
  const [enteringDemo, setEnteringDemo] = useState(false);
  const [demoError, setDemoError] = useState('');
  const [showMembers, setShowMembers] = useState(false);

  const [switchOpen, setSwitchOpen] = useState(false);
  const [teams, setTeams] = useState([]);
  const [teamsLoading, setTeamsLoading] = useState(false);
  const [teamsError, setTeamsError] = useState('');
  const switchRef = useRef(null);

  const currentTeamId = selectedTeam?.id ?? selectedTeam?._id ?? null;

  const teamSlug = teamParam
    ? decodeURIComponent(teamParam)
    : selectedTeam?.name || '';
  const currentTeamName = selectedTeam?.name || teamSlug || 'Workspace';

  const loadTeams = async () => {
    setTeamsError('');
    setTeamsLoading(true);
    try {
      const res = await axiosInstance.get('/team/details');
      setTeams(Array.isArray(res.data?.team) ? res.data.team : []);
    } catch (err) {
      setTeamsError(err?.response?.data?.message || 'Could not load your workspaces.');
    } finally {
      setTeamsLoading(false);
    }
  };

  const toggleSwitch = () => {
    setSwitchOpen((open) => {
      const next = !open;
      if (next) loadTeams();
      return next;
    });
  };

  // Close the workspace dropdown on outside click.
  useEffect(() => {
    if (!switchOpen) return undefined;
    const onDoc = (e) => {
      if (switchRef.current && !switchRef.current.contains(e.target)) setSwitchOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [switchOpen]);

  const switchTo = async (team) => {
    const id = team._id || team.id;
    if (String(id) === String(currentTeamId)) { setSwitchOpen(false); return; }
    if (setSelectedTeam) setSelectedTeam(team);
    // Persist as the user's default team on the backend (a real workspace switch,
    // unlike the demo "view as"). Best-effort — navigation proceeds regardless.
    try { if (id) await axiosInstance.post('/team/user/default-team', { teamId: id }); } catch (_) { /* non-fatal */ }
    setSwitchOpen(false);
    // The activation gate routes us into the app if that team is activated, or
    // to its own install wall if it isn't — without touching this team.
    navigate(`/${encodeURIComponent(team.name)}/chat`);
  };

  // Real site ID from the team payload; placeholder keeps the prompt readable
  // if it hasn't loaded yet (the deploy screen re-fetches, so this is transient).
  const siteId = selectedTeam?.siteId || '<your-site-id>';

  const prompt = useMemo(
    () =>
      `Install the Trodo agent-tracing skill from github.com/trodoai/skills\n`
      + `(npx skills add trodoai/skills), then follow it to add tracing to every agent\n`
      + `in this project. My Trodo site ID is ${siteId}. Map all agent entry points and\n`
      + `show me the run plan before writing code.`,
    [siteId],
  );

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(prompt);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* clipboard blocked — user can still select the text manually */
    }
  };

  const handleAgentDone = () => {
    const slug = encodeURIComponent(teamSlug);
    navigate(`/${slug}/install/deploy`);
  };

  const handleTryDemo = async () => {
    setDemoError('');
    setEnteringDemo(true);
    try {
      await enterDemoTeam(navigate, setSelectedTeam);
    } catch (err) {
      setDemoError(err?.response?.data?.message || err?.message || 'Could not open the demo.');
      setEnteringDemo(false);
    }
  };

  return (
    <div
      className="min-h-screen w-full flex flex-col items-center px-6 py-10"
      style={{ fontFamily: 'Inter, system-ui, sans-serif', backgroundColor: PANEL_BG }}
    >
      <div className="w-full max-w-2xl">
        <header className="flex items-center justify-between mb-10 gap-3">
          <TrodoWordmark />
          <div className="flex items-center gap-2">
            {/* Workspace switcher: the current workspace; opens a dropdown of the rest. */}
            <div className="relative" ref={switchRef}>
              <button
                type="button"
                onClick={toggleSwitch}
                aria-haspopup="listbox"
                aria-expanded={switchOpen}
                className="inline-flex items-center gap-2 py-2 pl-3.5 pr-2.5 rounded-lg text-[14px] font-medium border border-[color:var(--border-default)] text-[color:var(--text-primary)] hover:bg-[color:var(--surface-hover)] transition-colors"
              >
                <span className="max-w-[180px] truncate">{currentTeamName}</span>
                <ChevronDown size={16} strokeWidth={1.75} className={`shrink-0 transition-transform ${switchOpen ? 'rotate-180' : ''}`} />
              </button>
              {switchOpen && (
                <div
                  role="listbox"
                  className="absolute right-0 mt-2 w-64 z-20 rounded-xl border border-[color:var(--border-default)] bg-surface shadow-[0_16px_40px_-12px_rgba(15,23,42,0.3)] overflow-hidden"
                >
                  <div className="px-3 pt-2.5 pb-1.5 text-[11px] font-medium uppercase tracking-wide text-[color:var(--text-muted)]">
                    Switch workspace
                  </div>
                  <div className="max-h-[60vh] overflow-y-auto px-1 pb-1.5">
                    {teamsLoading && (
                      <div className="px-3 py-3 text-sm text-[color:var(--text-muted)]">Loading…</div>
                    )}
                    {teamsError && !teamsLoading && (
                      <div className="mx-2 my-1 p-2.5 rounded-lg text-[13px] bg-danger-soft text-danger border border-danger">{teamsError}</div>
                    )}
                    {!teamsLoading && !teamsError && teams.length <= 1 && (
                      <div className="px-3 py-3 text-sm text-[color:var(--text-muted)]">You only have this workspace.</div>
                    )}
                    {!teamsLoading && teams.map((t) => {
                      const tid = t._id || t.id;
                      const isCurrent = currentTeamId != null && String(tid) === String(currentTeamId);
                      return (
                        <button
                          key={tid}
                          type="button"
                          role="option"
                          aria-selected={isCurrent}
                          onClick={() => switchTo(t)}
                          className={`w-full flex items-center justify-between text-left px-2.5 py-2 rounded-lg transition-colors ${
                            isCurrent ? 'bg-[color:var(--surface-sunken)]' : 'hover:bg-[color:var(--surface-hover)]'
                          }`}
                        >
                          <span className="text-[14px] text-[color:var(--text-primary)] truncate">{t.name}</span>
                          {isCurrent && <Check size={15} strokeWidth={2} className="shrink-0 text-[color:var(--trodo-primary)]" />}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={() => setShowMembers(true)}
              className="inline-flex items-center gap-2 py-2 px-3.5 rounded-lg text-[14px] font-medium border border-[color:var(--border-default)] text-[color:var(--text-primary)] hover:bg-[color:var(--surface-hover)] transition-colors"
            >
              <UserPlus size={16} strokeWidth={1.75} />
              Invite developer
            </button>
          </div>
        </header>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
        >
          <h1 className="text-[2rem] leading-tight mb-3 text-[color:var(--text-primary)] font-normal tracking-tight">
            Install Trodo
          </h1>
          <p className="text-[15px] leading-relaxed text-[color:var(--text-muted)] mb-8">
            Paste this prompt in Cursor, Claude Code, Codex, or any agent. It runs the install
            skill end-to-end: adds the SDK, instruments your code, opens a PR.
          </p>

          {/* Prompt card */}
          <div className="rounded-xl border border-[color:var(--border-default)] bg-surface overflow-hidden">
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-[color:var(--border-default)]">
              <span className="text-xs font-medium tracking-wide uppercase text-[color:var(--text-muted)]">
                coding agent
              </span>
              <button
                type="button"
                onClick={handleCopy}
                className="text-xs font-medium px-2.5 py-1 rounded-md transition-colors text-ink-onBrand hover:opacity-[0.96]"
                style={{ backgroundColor: ACCENT }}
              >
                {copied ? 'Copied' : 'Copy'}
              </button>
            </div>
            <pre className="px-4 py-4 text-[14px] leading-relaxed text-[color:var(--text-primary)] whitespace-pre-wrap font-mono">
{prompt}
            </pre>
          </div>

          <p className="text-[13px] text-[color:var(--text-muted)] mt-4">
            Prefer to wire it up by hand?{' '}
            <a
              href={MANUAL_DOCS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-[color:var(--text-primary)] transition-colors"
            >
              Manual instrumentation guide
            </a>
          </p>

          <div className="mt-9">
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                onClick={handleTryDemo}
                disabled={enteringDemo}
                className={`flex-1 py-2.5 px-5 rounded-lg font-medium text-[15px] border border-[color:var(--border-default)] text-[color:var(--text-primary)] hover:bg-[color:var(--surface-hover)] transition-colors ${
                  enteringDemo ? 'opacity-60 cursor-not-allowed' : ''
                }`}
              >
                {enteringDemo ? 'Opening demo…' : 'Check out an example project'}
              </button>

              <button
                type="button"
                onClick={handleAgentDone}
                className="flex-1 py-2.5 px-5 rounded-lg font-medium text-[15px] text-ink-onBrand hover:opacity-[0.96] transition-opacity"
                style={{ backgroundColor: ACCENT }}
              >
                The agent is done →
              </button>
            </div>

            {demoError && (
              <div className="mt-3 p-3 rounded-lg text-sm bg-danger-soft text-danger border border-danger">
                {demoError}
              </div>
            )}
          </div>
        </motion.div>

        <footer className="pt-14 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-[color:var(--text-muted)]">
          <a href="https://docs.trodo.ai/" target="_blank" rel="noopener noreferrer" className="hover:text-[color:var(--text-primary)] transition-colors">
            Help
          </a>
          <a href="https://trodo.ai/privacy-policy" target="_blank" rel="noopener noreferrer" className="hover:text-[color:var(--text-primary)] transition-colors">
            Privacy
          </a>
          <a href="https://trodo.ai/terms-of-service" target="_blank" rel="noopener noreferrer" className="hover:text-[color:var(--text-primary)] transition-colors">
            Terms
          </a>
        </footer>
      </div>

      {/* Invite / team members popup: reuses the exact settings Members panel.
          z-index sits BELOW MUI's Dialog default (1300) so the panel's own
          "Add member" invite dialog renders ABOVE this overlay, not behind it. */}
      {showMembers && (
        <div
          className="fixed inset-0 flex items-start justify-center overflow-y-auto p-3 sm:p-6"
          style={{ backgroundColor: 'rgba(15,23,42,0.45)', zIndex: 1200 }}
          onMouseDown={() => setShowMembers(false)}
        >
          <div
            className="w-full max-w-5xl my-auto rounded-2xl border border-[color:var(--border-default)] bg-surface shadow-[0_20px_60px_-15px_rgba(15,23,42,0.35)]"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-[color:var(--border-default)]">
              <h2 className="text-[16px] font-semibold text-[color:var(--text-primary)]">Team members</h2>
              <button
                type="button"
                onClick={() => setShowMembers(false)}
                aria-label="Close"
                className="p-1.5 rounded-md text-[color:var(--text-muted)] hover:bg-[color:var(--surface-hover)] hover:text-[color:var(--text-primary)] transition-colors"
              >
                <X size={18} strokeWidth={1.75} />
              </button>
            </div>
            <div className="px-6 py-5 max-h-[82vh] overflow-y-auto">
              <MembersPage />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InstallProjectPage;
